import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  FileText,
  Globe,
  AlignLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Plus,
} from 'lucide-react'
import { useDocuments3Processing, type ExtractionProgress } from '@/hooks/useDocuments3Processing'
import { DocumentTab } from './DocumentTab'
import { WebUrlTab } from './WebUrlTab'
import type { ContentType } from '@/lib/api/client'

type SourceMode = null | 'document' | 'web' | 'paste'

const ALL_CONTENT_TYPES: Array<{ id: ContentType; label: string }> = [
  { id: 'custom', label: 'General' },
  { id: 'user_interview', label: 'User Interview' },
  { id: 'buyer_interview', label: 'Buyer Interview' },
  { id: 'stakeholder_meeting', label: 'Stakeholder Meeting' },
  { id: 'team_meeting', label: 'Team Meeting' },
  { id: 'transcript', label: 'Transcript' },
  { id: 'research_notes', label: 'Research Notes' },
  { id: 'vision_document', label: 'Vision Document' },
  { id: 'requirements_spec', label: 'Requirements Spec' },
  { id: 'competitor_analysis', label: 'Competitor Analysis' },
  { id: 'presentation', label: 'Presentation' },
]

interface UnifiedIngestionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UnifiedIngestionDialog({ open, onOpenChange }: UnifiedIngestionDialogProps) {
  const [mode, setMode] = useState<SourceMode>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [contentType, setContentType] = useState<ContentType>('custom')
  const [webComplete, setWebComplete] = useState(false)

  const {
    extractionProgress,
    isConfigured,
    uploadDocument,
    pasteContent,
    extractAndStore,
    resetProgress,
  } = useDocuments3Processing({ autoLoadDocuments: false })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const isExtracting = extractionProgress.stage === 'analyzing' || extractionProgress.stage === 'storing'
  const isProcessing = isSubmitting || isExtracting
  const isComplete = extractionProgress.stage === 'complete'

  const canSubmit = isConfigured && !isProcessing && !isComplete && title.trim().length > 0 && (
    file ? true : content.length >= 50
  )

  const disabledReason = !isConfigured
    ? 'API key not configured — go to Settings'
    : !title.trim()
      ? 'Enter a title'
      : !file && content.length < 50
        ? `Paste at least 50 characters (${content.length} now)`
        : undefined

  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      let doc
      if (file) {
        doc = await uploadDocument(file, contentType, title.trim())
      } else {
        doc = await pasteContent(title.trim(), content, contentType)
      }
      await extractAndStore(doc.document.id)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setMode(null)
    setTitle('')
    setContent('')
    setFile(null)
    setContentType('custom')
    setWebComplete(false)
    setSubmitError(null)
    setIsSubmitting(false)
    resetProgress()
  }

  const handleBack = () => {
    if (isProcessing) return
    handleReset()
  }

  const handleWebComplete = useCallback((_sourceId: string | null) => {
    setWebComplete(true)
  }, [])

  const handleClose = () => {
    if (!isProcessing) {
      handleReset()
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent style={{ maxWidth: '42rem', width: 'calc(100% - 2rem)' }} className="p-0 gap-0 !max-w-[42rem]" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Add content</DialogTitle>
        <div className="p-6">
          {/* Source Picker */}
          {mode === null && (
            <>
              <h2 className="font-display text-lg font-semibold mb-1">Add content</h2>
              <p className="text-sm text-text-muted mb-5">Bring in source material for entity extraction</p>
              <div className="flex flex-col gap-2.5">
                <SourceOption
                  icon={<FileText className="w-4 h-4" />}
                  label="Upload document"
                  desc="PDF, TXT, or Markdown file"
                  onClick={() => setMode('document')}
                />
                <SourceOption
                  icon={<Globe className="w-4 h-4" />}
                  label="Scrape web page"
                  desc="Import from URL with optional crawl"
                  onClick={() => setMode('web')}
                />
                <SourceOption
                  icon={<AlignLeft className="w-4 h-4" />}
                  label="Paste text"
                  desc="Interview, transcript, notes, or any text"
                  onClick={() => setMode('paste')}
                />
              </div>
            </>
          )}

          {/* Upload Document */}
          {mode === 'document' && (
            <>
              <BackLink onClick={handleBack} disabled={isProcessing} />
              <h2 className="font-display text-lg font-semibold mb-4">Upload document</h2>
              <DocumentTab
                content={content}
                onContentChange={setContent}
                file={file}
                onFileChange={setFile}
                onTitleSuggestion={(t) => { if (!title) setTitle(t) }}
                disabled={isProcessing || isComplete}
              />
              <div className="mt-3 space-y-3">
                <FormField label="Title">
                  <Input
                    placeholder="Auto-filled from file name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="h-8 text-sm"
                    disabled={isProcessing || isComplete}
                  />
                </FormField>
                <FormField label="Content type">
                  <ContentTypeSelect value={contentType} onChange={setContentType} disabled={isProcessing || isComplete} />
                </FormField>
              </div>
              <ProgressDisplay progress={extractionProgress} />
              {submitError && (
                <div className="flex items-start gap-2 p-3 mt-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{submitError}</p>
                </div>
              )}
              <ConfigWarning show={!isConfigured} />
              <SubmitActions
                isProcessing={isProcessing}
                isComplete={isComplete || webComplete}
                canSubmit={canSubmit}
                disabledReason={disabledReason}
                onSubmit={handleSubmit}
                onClose={handleClose}
                onReset={handleReset}
              />
            </>
          )}

          {/* Scrape Web Page */}
          {mode === 'web' && (
            <>
              <BackLink onClick={handleBack} disabled={isProcessing} />
              <h2 className="font-display text-lg font-semibold mb-4">Scrape web page</h2>
              <WebUrlTab disabled={isProcessing} onComplete={handleWebComplete} />
              {webComplete && (
                <div className="flex items-center gap-2 p-3 mt-3 bg-warm-50 border border-warm-200 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Entities extracted and stored</span>
                </div>
              )}
              {webComplete && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={handleClose}>Close</Button>
                  <Button size="sm" onClick={handleReset}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Add another
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Paste Text */}
          {mode === 'paste' && (
            <>
              <BackLink onClick={handleBack} disabled={isProcessing} />
              <h2 className="font-display text-lg font-semibold mb-4">Paste text</h2>
              <div className="space-y-3">
                <FormField label="Title">
                  <Input
                    placeholder="e.g., Sarah (PM) — User Interview"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="h-8 text-sm"
                    disabled={isProcessing || isComplete}
                  />
                </FormField>
                <FormField label="Type">
                  <ContentTypeSelect value={contentType} onChange={setContentType} disabled={isProcessing || isComplete} />
                </FormField>
                <div>
                  <textarea
                    placeholder="Paste your text here..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={7}
                    className="w-full px-2.5 py-2 text-sm font-mono border border-warm-300 rounded-md bg-white resize-y outline-none focus:border-warm-500"
                    disabled={isProcessing || isComplete}
                  />
                  <p className="text-xs text-text-muted mt-1">
                    {content.length} characters
                    {content.length > 0 && content.length < 50 && (
                      <span className="text-red-500"> (minimum 50)</span>
                    )}
                  </p>
                </div>
              </div>
              <ProgressDisplay progress={extractionProgress} />
              {submitError && (
                <div className="flex items-start gap-2 p-3 mt-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{submitError}</p>
                </div>
              )}
              <ConfigWarning show={!isConfigured} />
              <SubmitActions
                isProcessing={isProcessing}
                isComplete={isComplete}
                canSubmit={canSubmit}
                onSubmit={handleSubmit}
                onClose={handleClose}
                onReset={handleReset}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function SourceOption({ icon, label, desc, onClick }: {
  icon: React.ReactNode
  label: string
  desc: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-3.5 border border-warm-200 rounded-lg text-left hover:border-market/40 hover:bg-market/[0.03] transition-colors group"
    >
      <div className="w-9 h-9 rounded-lg bg-market/10 flex items-center justify-center text-market flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-text-muted">{desc}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-warm-400 group-hover:text-market transition-colors flex-shrink-0" />
    </button>
  )
}

function BackLink({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary mb-3 disabled:opacity-40"
    >
      <ChevronLeft className="w-3.5 h-3.5" />
      Back
    </button>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  )
}

function ContentTypeSelect({ value, onChange, disabled }: {
  value: ContentType
  onChange: (v: ContentType) => void
  disabled: boolean
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as ContentType)} disabled={disabled}>
      <SelectTrigger className="h-8 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ALL_CONTENT_TYPES.map((ct) => (
          <SelectItem key={ct.id} value={ct.id}>{ct.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function ConfigWarning({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <div className="flex items-center gap-2 p-2 mt-3 bg-amber-50 border border-amber-200 rounded-lg">
      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <span className="text-xs text-amber-800">API key not configured. Set it in Settings.</span>
    </div>
  )
}

function SubmitActions({ isProcessing, isComplete, canSubmit, disabledReason, onSubmit, onClose, onReset }: {
  isProcessing: boolean
  isComplete: boolean
  canSubmit: boolean
  disabledReason?: string
  onSubmit: () => void
  onClose: () => void
  onReset: () => void
}) {
  return (
    <div className="mt-4">
      {disabledReason && !isProcessing && !isComplete && (
        <p className="text-xs text-text-muted mb-2 text-right">{disabledReason}</p>
      )}
      <div className="flex justify-end gap-2">
        {isComplete ? (
          <>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            <Button size="sm" onClick={onReset}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add another
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={onClose} disabled={isProcessing}>Cancel</Button>
            <Button size="sm" onClick={onSubmit} disabled={!canSubmit}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Extracting...
                </>
              ) : (
                'Extract & store'
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

function ProgressDisplay({ progress }: { progress: ExtractionProgress }) {
  if (progress.stage === 'idle') return null

  return (
    <div className="bg-warm-50 border border-warm-200 rounded-lg p-3 mt-3">
      <div className="flex items-center gap-2 mb-1.5">
        {progress.stage === 'complete' ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
        ) : progress.stage === 'error' ? (
          <AlertCircle className="w-3.5 h-3.5 text-red-500" />
        ) : (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-warm-700" />
        )}
        <span className="text-xs font-medium">
          {progress.stage === 'analyzing' && 'Extracting entities...'}
          {progress.stage === 'storing' && 'Storing in graph...'}
          {progress.stage === 'complete' && `${progress.entitiesFound} entities extracted`}
          {progress.stage === 'error' && 'Extraction failed'}
        </span>
      </div>

      {(progress.stage === 'storing' || progress.stage === 'complete') && (
        <p className="text-xs text-text-secondary">
          {progress.entitiesFound} entities, {progress.relationshipsFound} relationships
        </p>
      )}

      {progress.metadata && (
        <p className="text-xs text-text-muted mt-1">
          {progress.metadata.provider}/{progress.metadata.model} &middot; {(progress.metadata.durationMs / 1000).toFixed(1)}s
        </p>
      )}

      {progress.error && (
        <p className="text-xs text-red-600 mt-1">{progress.error}</p>
      )}
    </div>
  )
}
